import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, ExternalLink, ChevronDown, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { InstallAppButton } from "../components/InstallAppButton";
import { VoteHeart } from "../components/VoteHeart";
import {
  NAV_LINKS,
  NAV_SECONDARY_LINKS,
  APP_URL,
} from "../lib/constants";
import { openSubscribeGate } from "../lib/subscription";

/**
 * Slim decision-focused site header.
 *
 * Structure (desktop): [logo]   [Features · Pricing · Why EVIQ · More▾ · FAQ]   [🌐] [Preview app →] [Get started]
 *
 * Design rationale: the old header had 10+ inline nav items. Visitors
 * couldn't parse the value prop through it. This version keeps only the
 * four decisions a shopper actually makes on this site — What is it?
 * (Features) · Why? (Why EVIQ) · How much? (Pricing) · Any doubts? (FAQ)
 * — and hides everything else behind a "More" dropdown for power-users.
 */
export const Header = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close "More" dropdown when clicking outside.
  useEffect(() => {
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const ROUTE_FOR = { features: "/features", pricing: "/pricing", blog: "/blog" };

  const handleAnchor = (id) => {
    setOpen(false);
    setMoreOpen(false);
    // Off the homepage: route to the dedicated page when one exists,
    // otherwise hard-navigate to `/#{id}` so anchors work.
    if (!isHome) {
      window.location.href = ROUTE_FOR[id] || `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else if (ROUTE_FOR[id]) window.location.href = ROUTE_FOR[id];
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="header-logo-button"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2 cursor-pointer"
          aria-label="EVIQ home"
        >
          <Logo size={28} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 lg:gap-2" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={ROUTE_FOR[l.id] || `/#${l.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleAnchor(l.id);
              }}
              data-testid={`nav-${l.id}`}
              className="text-sm text-zinc-300 hover:text-white transition-colors font-medium px-3 py-2 rounded-md hover:bg-white/[0.03] whitespace-nowrap"
            >
              {t(`nav.${l.tKey}`, l.label)}
            </a>
          ))}
          <Link
            to="/why-eviq"
            data-testid="nav-why-eviq"
            className="text-sm text-zinc-300 hover:text-white transition-colors font-medium px-3 py-2 rounded-md hover:bg-white/[0.03] whitespace-nowrap"
          >
            Why EVIQ?
          </Link>
          <Link
            to="/earth"
            data-testid="nav-earth"
            className="text-sm hover:text-white transition-colors font-medium px-3 py-2 rounded-md hover:bg-white/[0.03] inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ color: "#39FF14" }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
              <path d="M2 21c0-3 1.85-5.36 5.08-6" />
            </svg>
            Earth Initiative
          </Link>
          <Link
            to="/impact"
            data-testid="nav-impact"
            className="text-sm hover:text-white transition-colors font-medium px-3 py-2 rounded-md hover:bg-white/[0.03] inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{ color: "#00E5FF" }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 11.5 2 2 4-4" />
            </svg>
            Impact
          </Link>

          {/* "More" dropdown — home for the secondary anchors so the top bar stays clean */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen((v) => !v)}
              data-testid="nav-more-toggle"
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              className="text-sm text-zinc-300 hover:text-white transition-colors font-medium px-3 py-2 rounded-md hover:bg-white/[0.03] inline-flex items-center gap-1"
            >
              More
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`}
                strokeWidth={2.5}
              />
            </button>
            {moreOpen && (
              <div
                data-testid="nav-more-menu"
                role="menu"
                className="absolute right-0 mt-2 w-56 rounded-2xl border border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] p-2"
              >
                {NAV_SECONDARY_LINKS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => handleAnchor(l.id)}
                    data-testid={`nav-more-${l.id}`}
                    className="w-full text-left text-sm text-zinc-300 hover:text-white hover:bg-white/[0.04] rounded-lg px-3 py-2 transition-colors"
                  >
                    {t(`nav.${l.tKey}`, l.label)}
                  </button>
                ))}
                <div className="my-1 border-t border-white/10" />
                <Link
                  to="/about"
                  onClick={() => setMoreOpen(false)}
                  data-testid="nav-more-about"
                  className="block text-sm text-zinc-300 hover:text-white hover:bg-white/[0.04] rounded-lg px-3 py-2 transition-colors"
                >
                  About EVIQ
                </Link>
                <Link
                  to="/security"
                  onClick={() => setMoreOpen(false)}
                  data-testid="nav-more-security"
                  className="block text-sm text-zinc-300 hover:text-white hover:bg-white/[0.04] rounded-lg px-3 py-2 transition-colors"
                >
                  Security
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMoreOpen(false)}
                  data-testid="nav-more-contact"
                  className="block text-sm text-zinc-300 hover:text-white hover:bg-white/[0.04] rounded-lg px-3 py-2 transition-colors"
                >
                  Contact
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Desktop actions */}
        <div className="hidden md:flex items-center gap-2">
          <VoteHeart />
          <InstallAppButton />
          <LanguageSwitcher compact />
          <Link
            to={APP_URL}
            data-testid="header-preview-link"
            className="text-sm font-medium text-zinc-400 hover:text-white transition-colors px-3 py-2 inline-flex items-center gap-1.5"
          >
            Preview
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </Link>
          <button
            type="button"
            onClick={() => openSubscribeGate("header")}
            data-testid="header-cta-button"
            className="brand-gradient text-black font-semibold text-sm rounded-full px-4 py-2.5 hover:opacity-90 transition-opacity"
          >
            Get started
          </button>
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-2">
          <VoteHeart compact />
          <LanguageSwitcher compact />
          <button
            className="text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu-panel"
          >
            {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div
          id="mobile-menu-panel"
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl max-h-[calc(100vh-4rem)] overflow-y-auto"
          data-testid="mobile-menu-panel"
        >
          <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={ROUTE_FOR[l.id] || `/#${l.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleAnchor(l.id);
                }}
                data-testid={`mobile-nav-${l.id}`}
                className="text-left text-white text-base font-medium py-2.5"
              >
                {t(`nav.${l.tKey}`, l.label)}
              </a>
            ))}
            <Link
              to="/why-eviq"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-why-eviq"
              className="text-white text-base font-medium py-2.5"
            >
              Why EVIQ?
            </Link>
            <Link
              to="/earth"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-earth"
              className="text-base font-medium py-2.5 inline-flex items-center gap-2"
              style={{ color: "#39FF14" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                <path d="M2 21c0-3 1.85-5.36 5.08-6" />
              </svg>
              Earth Initiative
            </Link>
            <Link
              to="/impact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-impact"
              className="text-base font-medium py-2.5 inline-flex items-center gap-2"
              style={{ color: "#00E5FF" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 11.5 2 2 4-4" />
              </svg>
              Impact
            </Link>

            {/* More section — quiet secondary links */}
            <div className="pt-4 mt-2 border-t border-white/10">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">
                More
              </div>
              {NAV_SECONDARY_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => handleAnchor(l.id)}
                  data-testid={`mobile-nav-more-${l.id}`}
                  className="w-full text-left text-zinc-400 hover:text-white text-sm py-1.5"
                >
                  {t(`nav.${l.tKey}`, l.label)}
                </button>
              ))}
              <Link
                to="/about"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-more-about"
                className="block text-zinc-400 hover:text-white text-sm py-1.5"
              >
                About EVIQ
              </Link>
              <Link
                to="/security"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-more-security"
                className="block text-zinc-400 hover:text-white text-sm py-1.5"
              >
                Security
              </Link>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-more-contact"
                className="block text-zinc-400 hover:text-white text-sm py-1.5"
              >
                Contact
              </Link>
            </div>

            <div className="pt-4 mt-2 border-t border-white/10 flex flex-col gap-3">
              <InstallAppButton className="justify-center" />
              <Link
                to={APP_URL}
                onClick={() => setOpen(false)}
                data-testid="mobile-preview-link"
                className="text-zinc-300 text-base text-center py-2"
              >
                Preview the app →
              </Link>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openSubscribeGate("mobile-header");
                }}
                data-testid="mobile-cta-button"
                className="brand-gradient text-black font-semibold rounded-full px-5 py-3 text-center"
              >
                Get started
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
